import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useState } from "react";
import ReferringAgencySelect from "@/components/app/admin/provider-groups/ReferringAgencySelect";
import CustomTitle from "@/components/app/CustomTitle";
import CustomDatePicker from "@/components/CustomDatePicker";
import CustomSelect from "@/components/CustomSelect";
import CustomSelectScroll from "@/components/CustomSelectScroll";
import Flex from "@/components/Flex";
import OutlineCard from "@/components/OutlineCard";
import { COMMUNICATION_TYPE } from "@/constants/master";
import { NewPatientForm } from "@/constants/provider/patient/add-new-patient/add-new-patient-form";
import NewReferringProvider from "@/pages/app/provider/collabration/referrals/NewReferringProvider";
import { useLocationsList } from "@/queries/use-locationList";
import { useProviderListV2WithoutReferralFilter } from "@/queries/use-provider-list-v2";
import { getStoredRealm } from "@/utils/auth-storage";
import { DEF_PAGINATION, DGT_Pagination } from "@/utils/utils";

interface ProviderInformationProps {
  formik: FormikProps<NewPatientForm>;
}

const ProviderInformation = ({ formik }: ProviderInformationProps) => {
  const [searchProviderKey, setSearchProviderKey] = useState<string>("");
  const [searchProviderGroupKey, setSearchProviderGroupKey] =
    useState<string>("");
  const [providerPagination, setProviderPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [locationPagination, setLocationPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [isAddPcpOpen, setIsAddPcpOpen] = useState<boolean>(false);
  const { values, setFieldValue } = formik;
  const xTENANTID = getStoredRealm() ?? "";
  const { data: providersList, isFetching: providersListFetching } =
    useProviderListV2WithoutReferralFilter({
      pagination: providerPagination,
      status: true,
      searchString: searchProviderKey,
      xTENANTID,
    });

  const {
    data: providerGroupLocationList,
    isFetching: providerGroupLocationFetching,
  } = useLocationsList({
    pagination: locationPagination,
    searchKey: searchProviderGroupKey,
    status: true,
    providerGroupId: xTENANTID,
  });

  return (
    <Box id={"Provider Information"}>
      <CustomTitle mb={1}>Provider Information</CustomTitle>

      <OutlineCard>
        <Grid container spacing={2} direction={"column"} p={0.8}>
          <Grid item container spacing={2}>
            <Grid item xs={3}>
              <CustomSelectScroll
                label="Provider Group Location"
                // placeholder="Select Provider Group Location"
                loading={providerGroupLocationFetching}
                options={providerGroupLocationList?.data?.content?.map(
                  (it) => ({
                    label: it.name,
                    value: it.uuid,
                  })
                )}
                onSearch={(_, val) => {
                  setSearchProviderGroupKey(val);
                  setLocationPagination((p) => ({ ...p, page: 0 }));
                }}
                pagination={locationPagination}
                setPagination={setLocationPagination}
                totalElements={
                  providerGroupLocationList?.data?.page?.totalElements
                }
                value={Object.keys(values.locationId ?? {})?.[0] ?? undefined}
                onChange={(value, option) => {
                  // clear provider after clear location
                  if (!value) {
                    setFieldValue("providerId", undefined);
                  }

                  setFieldValue(
                    "locationId",
                    value && option
                      ? {
                          [value]: option.label,
                        }
                      : undefined
                  );
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Flex alignItems="flex-end" gap={0.5}>
                <Box flex={1}>
                  <CustomSelectScroll
                    label="Primary Care Provider (PCP)"
                    loading={providersListFetching}
                    options={providersList?.data?.content?.map((it) => ({
                      label: it?.name,
                      value: it.uuid,
                    }))}
                    onSearch={(_, val) => {
                      setSearchProviderKey(val);
                      setProviderPagination((p) => ({ ...p, page: 0 }));
                    }}
                    pagination={providerPagination}
                    setPagination={setProviderPagination}
                    totalElements={providersList?.data?.page?.totalElements}
                    value={Object.keys(values.providerId ?? {})?.[0]}
                    onChange={(value, option) => {
                      setFieldValue(
                        "providerId",
                        value && option
                          ? {
                              [value]: option.label,
                            }
                          : undefined
                      );
                    }}
                  />
                </Box>
                <Tooltip title="Add New PCP">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setIsAddPcpOpen(true)}
                    sx={{ mb: 0.5 }}
                  >
                    <PersonAddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Flex>
            </Grid>
            <Grid item xs={3}>
              <ReferringAgencySelect
                value={values.referringAgencies?.[0]?.uuid ?? null}
                onChange={(uuid) =>
                  setFieldValue("referringAgencies", uuid ? [{ uuid }] : [])
                }
              />
            </Grid>
            <Grid item xs={3}>
              <CustomDatePicker
                label="Registration Date"
                maxDate={dayjs(new Date())}
                value={
                  values.registrationDate
                    ? dayjs(values.registrationDate)
                    : null
                }
                onChange={(value) => setFieldValue("registrationDate", value)}
                // placeholder="Choose Date"
              />
            </Grid>
            <Grid item xs={3}>
              <CustomSelect
                options={COMMUNICATION_TYPE}
                value={values.defaultCommunicationChannel}
                onChange={(value) => {
                  setFieldValue("defaultCommunicationChannel", value);
                }}
                label="Default Communication Channel"
                // required={true}
                // error={touched?.maritalStatus && errors?.maritalStatus}
              />
            </Grid>
          </Grid>
        </Grid>
      </OutlineCard>

      {isAddPcpOpen && (
        <NewReferringProvider
          open={isAddPcpOpen}
          onClose={() => setIsAddPcpOpen(false)}
          referralInOut="Receiving"
        />
      )}
    </Box>
  );
};

export default ProviderInformation;
